import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Check if Supabase is configured - allow app to work without it for local dev
// Accept both JWT format (eyJ...) and newer sb_publishable_ format
const isSupabaseConfigured = supabaseUrl && supabaseAnonKey &&
  (supabaseAnonKey.startsWith('eyJ') || supabaseAnonKey.startsWith('sb_'));

// Create a mock client if Supabase is not configured
const createMockClient = () => ({
  auth: {
    getSession: async () => ({ data: { session: null }, error: null }),
    getUser: async () => ({ data: { user: null }, error: null }),
    signUp: async () => ({ data: null, error: new Error('Supabase not configured') }),
    signInWithPassword: async () => ({ data: null, error: new Error('Supabase not configured') }),
    signInWithOAuth: async () => ({ data: null, error: new Error('Supabase not configured') }),
    signOut: async () => ({ error: null }),
    resetPasswordForEmail: async () => ({ data: null, error: new Error('Supabase not configured') }),
    updateUser: async () => ({ data: null, error: new Error('Supabase not configured') }),
    onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
  },
  from: () => ({
    select: () => ({ eq: () => ({ single: async () => ({ data: null, error: null }), order: async () => ({ data: [], error: null }), not: () => ({ not: async () => ({ count: 0, error: null }) }) }) }),
    insert: () => ({ select: () => ({ single: async () => ({ data: null, error: new Error('Supabase not configured') }) }) }),
    update: () => ({ eq: () => ({ select: () => ({ single: async () => ({ data: null, error: new Error('Supabase not configured') }) }) }) }),
    delete: () => ({ eq: async () => ({ error: new Error('Supabase not configured') }) }),
    upsert: () => ({ select: () => ({ single: async () => ({ data: null, error: new Error('Supabase not configured') }) }) }),
  }),
});

export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    })
  : createMockClient();

export const isSupabaseEnabled = isSupabaseConfigured;

// Log warning if not configured
if (!isSupabaseConfigured) {
  console.warn('Supabase not configured - using localStorage for data storage. Add valid VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to .env.local');
}

// Auth helpers
export const signUp = async (email, password, metadata = {}) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: metadata,
    },
  });
  return { data, error };
};

export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  return { data, error };
};

export const signInWithGoogle = async () => {
  const redirectUrl = import.meta.env.PROD
    ? 'https://app.sponsy.app/auth/callback'
    : `${window.location.origin}/auth/callback`;
    
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: redirectUrl,
    },
  });
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const resetPassword = async (email) => {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/auth/reset-password`,
  });
  return { data, error };
};

export const updatePassword = async (newPassword) => {
  const { data, error } = await supabase.auth.updateUser({
    password: newPassword,
  });
  return { data, error };
};

export const getSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  return { session, error };
};

export const getUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  return { user, error };
};

// Database helpers
export const db = {
  // Profiles
  async getProfile(userId) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    return { data, error };
  },

  async updateProfile(userId, updates) {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();
    return { data, error };
  },

  // Subscriptions
  async getSubscription(userId) {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active')
      .single();
    return { data, error };
  },

  // Brands
  async getBrands(userId) {
    const { data, error } = await supabase
      .from('brands')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  async createBrand(brand) {
    const { data, error } = await supabase
      .from('brands')
      .insert(brand)
      .select()
      .single();
    return { data, error };
  },

  async updateBrand(brandId, updates) {
    const { data, error } = await supabase
      .from('brands')
      .update(updates)
      .eq('id', brandId)
      .select()
      .single();
    return { data, error };
  },

  async deleteBrand(brandId) {
    const { error } = await supabase
      .from('brands')
      .delete()
      .eq('id', brandId);
    return { error };
  },

  // Deals
  async getDeals(userId) {
    const { data, error } = await supabase
      .from('deals')
      .select(`
        *,
        brand:brands(id, name, domain, industry)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  async createDeal(deal) {
    const { data, error } = await supabase
      .from('deals')
      .insert(deal)
      .select()
      .single();
    return { data, error };
  },

  async updateDeal(dealId, updates) {
    const { data, error } = await supabase
      .from('deals')
      .update(updates)
      .eq('id', dealId)
      .select()
      .single();
    return { data, error };
  },

  async deleteDeal(dealId) {
    const { error } = await supabase
      .from('deals')
      .delete()
      .eq('id', dealId);
    return { error };
  },

  // Contracts
  async getContracts(userId) {
    const { data, error } = await supabase
      .from('contracts')
      .select(`
        *,
        deal:deals(id, title),
        brand:brands(id, name)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  async createContract(contract) {
    const { data, error } = await supabase
      .from('contracts')
      .insert(contract)
      .select()
      .single();
    return { data, error };
  },

  async updateContract(contractId, updates) {
    const { data, error } = await supabase
      .from('contracts')
      .update(updates)
      .eq('id', contractId)
      .select()
      .single();
    return { data, error };
  },

  async deleteContract(contractId) {
    const { error } = await supabase
      .from('contracts')
      .delete()
      .eq('id', contractId);
    return { error };
  },

  // Invoices
  async getInvoices(userId) {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        deal:deals(id, title),
        brand:brands(id, name)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  async createInvoice(invoice) {
    const { data, error } = await supabase
      .from('invoices')
      .insert(invoice)
      .select()
      .single();
    return { data, error };
  },

  async updateInvoice(invoiceId, updates) {
    const { data, error } = await supabase
      .from('invoices')
      .update(updates)
      .eq('id', invoiceId)
      .select()
      .single();
    return { data, error };
  },

  async deleteInvoice(invoiceId) {
    const { error } = await supabase
      .from('invoices')
      .delete()
      .eq('id', invoiceId);
    return { error };
  },

  // Media Kits
  async getMediaKit(userId) {
    const { data, error } = await supabase
      .from('media_kits')
      .select('*')
      .eq('user_id', userId)
      .single();
    return { data, error };
  },

  async upsertMediaKit(mediaKit) {
    const { data, error } = await supabase
      .from('media_kits')
      .upsert(mediaKit)
      .select()
      .single();
    return { data, error };
  },

  // Deal count for subscription limits
  async getActiveDealsCount(userId) {
    const { count, error } = await supabase
      .from('deals')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .not('stage', 'eq', 'completed')
      .not('stage', 'eq', 'lost');
    return { count, error };
  },
};

export default supabase;
